/**
 * World Garden
 *
 * A web reimagining of Niantic's Emoji Garden, rebuilt on Three.js + WebXR.
 * Place a garden plot on a real surface wherever you are, sow drought-hardy
 * species, and tap them up through five growth stages — each tap ringing the
 * next note of that species' scale.
 *
 * This file is the shell: lifecycle, input routing, the game events and the
 * render loop. Progression, sharing and the world map are in
 * `src/app/`; this game's data is in `src/content/`.
 */

import * as THREE from 'three';

import './style.css';
import {
  createBackend,
  detectCapabilities,
  type ARBackend,
  type ARMode,
} from './core/ARSession';
import { AudioEngine } from './core/AudioEngine';
import { ParticleSystem } from './core/Particles';
import {
  emptyProfile,
  loadProfile,
  makeDebouncedSaver,
  saveProfile,
  type SavedProfile,
} from './core/Storage';
import { Garden, type AnyGardenSave, type OfflineReport } from './game/Garden';
import { factById, nextFact, TOTAL_FACTS } from './content/facts';
import { BADGES_BY_ID, earnedBadges, type BadgeId } from './content/badges';
import { STAGES, type Plant } from './game/Plant';
import {
  currentStreak,
  formatDuration,
  makeStamp,
  pointsFor,
  rankRegister,
  sanitiseField,
  scoreFor,
  MAX_FROM,
  MAX_NAME,
} from './game/progress';
import {
  DEFAULT_SPECIES,
  PACK_DRAW,
  SPECIES,
  SPECIES_ORDER,
  type SpeciesId,
} from './content/species';
import { PACK_COST, PACK_SIZE, drawPack } from './game/economy';
import { TIERS } from './content/tiers';
import { nextTier, tierFor, tierForSlot } from './game/tiers';
import { decodeGarden, seedFromLocation, type SharedGarden } from './game/share';
import { weatherAt } from './game/weather';
import {
  WEATHER,
  WEATHER_ORDER,
  type Mutation,
  type MutationId,
  type WeatherId,
} from './content/weather';
import { buildHorizon, type Horizon } from './models/horizon';
import { ProgressionAdapter } from './app/progression';
import { ShareSheet } from './app/share';
import { WorldMap } from './app/world';
import { buildReticle } from './models/reticle';
import { UI } from './ui/UI';

type Phase = 'intro' | 'scanning' | 'play';

class App {
  private renderer: THREE.WebGLRenderer;
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  /* `Timer` (not the deprecated `Clock`) — it snapshots delta and elapsed on
     `update()`, so every system in a frame reads identical values. */
  private timer = new THREE.Timer();

  private audio = new AudioEngine();
  private particles = new ParticleSystem();
  private reticle = buildReticle();
  private garden: Garden;
  private ui: UI;

  private backend: ARBackend | null = null;
  private mode: ARMode = 'camera';
  private phase: Phase = 'intro';

  private stage = document.getElementById('stage')!;
  private overlayRoot = document.getElementById('ui')!;

  private save = makeDebouncedSaver();
  /** Always present. Loaded on boot, or a fresh one for a first-time player. */
  private profile: SavedProfile;
  /** A saved garden waiting for a plot to be placed before it can be rebuilt. */
  private pendingRestore: { save: AnyGardenSave; lastSeenAt: number } | null = null;
  private factsSeen = new Set<string>();
  private badges = new Set<BadgeId>();
  /**
   * Every species ever grown here, standing or since harvested.
   *
   * The garden only knows what is in it right now, so lifting a specimen
   * would otherwise retract the Botanist badge and the species bonus it was
   * worth. Same self-healing shape as `mutations`.
   */
  private speciesGrown = new Set<SpeciesId>();
  private hasSownOnce = false;
  private hasGrownOnce = false;
  private sunTarget = new THREE.Object3D();
  private hemi!: THREE.HemisphereLight;
  private sun!: THREE.DirectionalLight;
  /** Baseline intensities, so weather scales them rather than replacing them. */
  private baseHemi = 0;
  private baseSun = 0;
  /* Only ever non-null in `preview`. See `models/horizon.ts` — a sixty-metre
     sand plane would draw straight over a live camera feed. */
  private horizon: Horizon | null = null;
  private mutations = new Set<MutationId>();
  private weatherSeen = new Set<WeatherId>();
  private moteAccumulator = 0;
  private lastCountdown = 0;
  private lastHeartbeat = 0;
  private hiddenAt: number | null = null;
  /*
   * The pieces of the shell that live in `src/app/`. Each is handed what it
   * reads as functions rather than values, because `App` replaces its profile
   * and its record sets wholesale and a captured reference would go stale.
   */
  private progression: ProgressionAdapter;
  private sharing: ShareSheet;
  /** The world garden. The globe and any account screens load on first open. */
  private world: WorldMap;

  /**
   * Set when the app was opened on somebody else's share link.
   *
   * While this is non-null **nothing is written**: `persist` and `flush` both
   * return early. A visitor's own garden, register and streak have to survive
   * looking at a friend's link untouched, and the cheapest way to guarantee
   * that is for the write path not to run at all.
   */
  private visiting: SharedGarden | null = null;

  constructor() {
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true, // the camera feed shows through
      powerPreference: 'high-performance',
    });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setClearAlpha(0);
    this.renderer.shadowMap.enabled = true;
    // PCFSoftShadowMap is deprecated in r185 and silently falls back to this
    // one anyway, so asking for it only bought a console warning.
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.renderer.xr.enabled = true;
    this.stage.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(
      62,
      window.innerWidth / window.innerHeight,
      0.01,
      50,
    );

    this.setupLighting();
    this.scene.add(this.reticle.root);
    this.scene.add(this.particles.points);

    this.garden = new Garden(this.scene, this.audio, this.particles, {
      onSow: (p) => this.handleSow(p),
      onTend: () => {
        this.progression.bump('tended');
        this.progression.advance();
      },
      onGrow: (p, byTending) => this.handleGrow(p, byTending),
      onMature: (p, count) => this.handleMature(p, count),
      onAlreadyMature: () => this.handleAlreadyMature(),
      onHarvestOffer: (p, paid) => this.handleHarvestOffer(p, paid),
      onHarvest: (p, paid) => this.handleHarvest(p, paid),
      onHarvestDisarm: () => this.ui.hideHint(),
      onFull: () =>
        this.ui.toast(
          'Garden full',
          'Every bed is planted. Tend one along, or tap a full-grown one twice to harvest it.',
        ),
    });

    this.ui = new UI(
      {
        onBegin: () => void this.begin(),
        onPlace: () => this.placeGarden(),
        onSpeciesChange: (id) => this.handleSpeciesChange(id),
        onToggleMute: () => this.toggleMute(),
        onMoveGarden: () => this.moveGarden(),
        onShowRegister: () => this.showRegister(),
        onShowJournal: () => this.progression.showJournal(),
        onShowWorld: () => void this.world.show(),
        onOpenPack: () => this.openPack(),
        onShowShare: () => this.sharing.show(),
        onShareNameChange: () => this.sharing.refresh(),
        onSaveImage: () => void this.sharing.saveCard(),
        onCopyLink: () => void this.sharing.copyLink(),
        onLeaveVisit: () => this.leaveVisit(),
        onSign: (name, from) => this.signRegister(name, from),
      },
      { species: SPECIES_ORDER.map((id) => SPECIES[id]), selected: DEFAULT_SPECIES },
    );

    const { ui, audio } = this;
    this.progression = new ProgressionAdapter({
      ui,
      audio,
      profile: () => this.profile,
      visiting: () => this.visiting !== null,
      factsSeen: () => this.factsSeen,
      speciesGrown: () => this.speciesGrown,
      mutations: () => this.mutations,
      badges: () => this.badges,
      persist: () => this.persist(),
      refreshHud: () => this.refreshHud(),
    });
    this.sharing = new ShareSheet({
      ui,
      audio,
      renderer: this.renderer,
      scene: this.scene,
      camera: this.camera,
      garden: this.garden,
      backend: () => this.backend,
      playing: () => this.phase === 'play',
      visiting: () => this.visiting !== null,
      activeStamp: () => this.activeStamp,
    });
    this.world = new WorldMap({
      ui,
      audio,
      // A borrowed garden is not the player's to publish.
      publishable: () =>
        this.visiting
          ? null
          : {
              treesGrown: this.profile.treesGrown,
              tier: this.garden.tier,
              seed: this.sharing.seed(false),
            },
      // `phase` drops back to 'intro' when an immersive session ends.
      immersive: () => this.mode === 'webxr' && this.phase !== 'intro',
    });

    const now = Date.now();
    const loaded = loadProfile();
    this.profile = loaded ?? emptyProfile(now);
    this.factsSeen = new Set(this.profile.factsSeen);
    this.badges = new Set(this.profile.badges as BadgeId[]);
    this.mutations = new Set(this.profile.mutationsSeen as MutationId[]);
    this.speciesGrown = new Set(this.profile.speciesGrown);
    this.weatherSeen = new Set(this.profile.weatherSeen as WeatherId[]);
    this.audio.setMuted(this.profile.muted);
    this.ui.setMuted(this.profile.muted);

    /*
     * Open today's and this week's challenge windows *before* logging the
     * visit. A window snapshots the counters it opens with, and `days` is one
     * of them — logged first, "visit on three days this week" would silently
     * need four.
     *
     * `days` is seeded from the visit log the first time this build runs, so a
     * returning player's history counts toward their level. The log itself is
     * still written before anything reads the streak.
     */
    this.progression.seedDays();
    this.progression.rollChallenges(now);
    this.progression.noteVisit(now);

    /*
     * A share link outranks the saved garden. The visitor's own profile is
     * loaded as normal — the streak, the register and the badges are still
     * theirs — but their plot is not rebuilt, and `persist`/`flush` are
     * disabled for the whole visit so nothing they see can overwrite it.
     */
    const seed = seedFromLocation();
    this.visiting = seed ? decodeGarden(seed) : null;

    if (!this.visiting && loaded && loaded.garden.plants.length > 0) {
      this.pendingRestore = { save: loaded.garden, lastSeenAt: loaded.lastSeenAt };
    }
    if (seed && !this.visiting) {
      this.ui.toast('That link did not open', 'The garden in it could not be read.');
    }

    window.addEventListener('resize', this.onResize);
    document.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    document.addEventListener('pointerup', this.onPointerUp, { passive: true });
    /*
     * Growth is measured from `lastSeenAt`, so a stale one makes the
     * welcome-back summary claim credit for time the player spent staring at
     * the garden. Flush synchronously the moment the page is hidden — on iOS
     * `pagehide` is frequently the last callback a tab ever gets.
     */
    document.addEventListener('visibilitychange', () => {
      const hidden = document.visibilityState === 'hidden';
      /*
       * Passers-by are the first thing to stop costing anything. `rAF` is
       * suspended while hidden so this is belt and braces on desktop, but an
       * XR session keeps its own animation loop running when the page is not
       * visible — and there the freeze is real.
       */
      this.garden.setVisitorsEnabled(!hidden && this.phase === 'play');
      if (hidden) {
        this.hiddenAt = Date.now();
        this.flush();
      } else {
        this.resume();
      }
    });
    window.addEventListener('pagehide', () => this.flush());

    void this.probeCapabilities();
    this.renderer.setAnimationLoop(this.tick);

    // Dev-only inspection handle. Stripped from production builds by the
    // `import.meta.env.DEV` guard, which Vite folds to `false` and drops.
    if (import.meta.env.DEV) {
      const w = window as unknown as Record<string, unknown>;
      w.__garden = this;
      /*
       * What the headset actually granted. Every AR capability past hit-test
       * is optional and absent on most hardware, so on a device this is the
       * only way to tell whether occlusion is live or whether the runtime
       * quietly handed back the CPU depth path that three.js ignores.
       */
      Object.defineProperty(w, '__garden_ar', {
        configurable: true,
        get: () => this.backend?.features?.() ?? [],
      });
      // Render and post the framebuffer to the dev server. The render and the
      // read must happen in one task — without `preserveDrawingBuffer` the
      // buffer is cleared as soon as control returns to the browser.
      w.__shot = (name: string) => {
        this.renderer.render(this.scene, this.camera);
        const dataUrl = this.renderer.domElement.toDataURL('image/png');
        return fetch('/__shot', {
          method: 'POST',
          body: JSON.stringify({ name, dataUrl }),
        }).then((r) => r.text());
      };
    }
  }

  /* ---------------------------------------------------------------- *
   * Setup
   * ---------------------------------------------------------------- */

  private setupLighting(): void {
    // Sky/ground bounce approximating a bright dry-climate day: warm above,
    // sand-reflected fill below.
    const hemi = new THREE.HemisphereLight(0xfff3dd, 0xbfa77f, 2.1);
    this.scene.add(hemi);
    this.hemi = hemi;
    this.baseHemi = hemi.intensity;

    const sun = new THREE.DirectionalLight(0xfff0d4, 2.3);
    sun.position.set(1.6, 3.2, 1.1);
    sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024);
    sun.shadow.camera.near = 0.1;
    sun.shadow.camera.far = 12;
    /*
     * Tight frustum — a loose one is what turns AR shadows into a blurry
     * smear. Widened from 1.2 to take in the pavilion walls at 0.86 m and
     * their merlons; any further and the plants lose their contact shadows,
     * which are most of what sells the garden as physically present.
     */
    const s = 1.55;
    sun.shadow.camera.left = -s;
    sun.shadow.camera.right = s;
    sun.shadow.camera.top = s;
    sun.shadow.camera.bottom = -s;
    sun.shadow.bias = -0.0012;
    sun.shadow.normalBias = 0.02;
    this.scene.add(sun);
    this.scene.add(this.sunTarget);
    sun.target = this.sunTarget;
    this.sun = sun;
    this.baseSun = sun.intensity;

    // Cool rim from the opposite side keeps foliage from going flat.
    const rim = new THREE.DirectionalLight(0xbfd9ff, 0.5);
    rim.position.set(-1.8, 1.4, -1.6);
    this.scene.add(rim);
  }

  private async probeCapabilities(): Promise<void> {
    const caps = await detectCapabilities();
    this.mode = caps.preferred;

    switch (this.mode) {
      case 'webxr':
        this.ui.setModeNote(
          'Full AR · surface tracking',
          'Your device supports surface tracking, so you can walk around your garden.',
        );
        break;
      case 'camera':
        this.ui.setModeNote(
          'Camera AR',
          'Your browser does not support full surface tracking, so the garden ' +
            'anchors to a fixed spot on the ground. Look around by moving your phone.',
        );
        break;
      case 'preview':
        this.ui.setModeNote(
          'Preview · no camera',
          caps.degradedReason
            ? `${caps.degradedReason} You can still grow a garden — drag to look around it. ` +
              'Open this page on a phone for the full AR experience.'
            : 'Drag to look around your garden. Open on a phone for AR.',
        );
        break;
    }

    this.ui.setBeginEnabled(true);
  }

  /* ---------------------------------------------------------------- *
   * Flow
   * ---------------------------------------------------------------- */

  private async begin(): Promise<void> {
    this.ui.setBeginEnabled(false);
    try {
      // Audio must be unlocked inside the gesture that started this.
      await this.audio.unlock();
      this.audio.tick();

      this.backend = createBackend({
        mode: this.mode,
        renderer: this.renderer,
        camera: this.camera,
        container: this.stage,
        overlayRoot: this.overlayRoot,
        onEnd: () => this.handleSessionEnd(),
      });

      await this.backend.start();
      this.audio.startAmbience();

      if (this.mode === 'preview') {
        // No camera feed to protect, so give the garden a world to sit in.
        // A failed camera start drops to preview and calls `begin()` again,
        // so clear any previous world before building another.
        this.disposeHorizon();
        /*
         * Haze the far dunes into the sky. `near` is well past anything in
         * the garden, so the plot and plants are untouched — and it closes in
         * early on purpose, because an unfogged flat sand disc reaching forty
         * metres is a lot of nothing, and haze is what turns it into distance.
         * The horizon owns the colour and keeps it in step with the weather.
         */
        const fog = new THREE.Fog(0xe8cfa6, 5, 30);
        this.scene.fog = fog;
        this.horizon = buildHorizon(fog);
        this.scene.add(this.horizon.root);
        // Nothing to scan for — put the plot down and start playing.
        this.placeGarden();
        return;
      }

      this.phase = 'scanning';
      this.ui.show('scanning');
      this.ui.setScanText(
        this.mode === 'webxr'
          ? 'Point at the ground and move your phone slowly'
          : 'Hold your phone up and point at the ground in front of you',
      );
    } catch (err) {
      // A refused camera or a cancelled XR session should not be the end of
      // the road — drop to preview and tell the player why.
      if (this.mode !== 'preview') {
        this.backend?.stop();
        this.mode = 'preview';
        this.ui.toast('AR unavailable', describeStartError(err), 5200);
        void this.begin();
        return;
      }
      this.ui.setBeginEnabled(true);
      this.ui.showBlocked(describeStartError(err));
    }
  }

  private placeGarden(): void {
    const hit = this.backend?.hitTest();
    if (!hit) return;

    /*
     * Open the garden before anything is placed or restored: a saved plant in
     * a tier-3 bed has nowhere to stand until that district exists, and
     * `Garden.restore` drops such a plant in silence — after which the next
     * `serialize()` writes the loss to disk.
     *
     * Whichever is larger, then: what the player earned, or what the garden
     * waiting to be rebuilt actually needs. The two should always agree, since
     * the tree count is sticky and gates only ever open. Taking the max is how
     * that stays true rather than merely likely.
     */
    const earned = tierFor(this.profile.treesGrown);
    const needed = this.pendingRestore
      ? this.pendingRestore.save.plants.reduce(
          (max, p) => Math.max(max, tierForSlot(p.slot)),
          0,
        )
      : 0;
    this.garden.setTier(Math.max(earned, needed));

    this.garden.place(hit.position, hit.quaternion);
    // Point the sun at the plot so the shadow frustum actually contains it.
    this.sunTarget.position.copy(hit.position);

    this.reticle.setVisible(false);
    this.phase = 'play';
    this.ui.show('play');
    // The garden exists now, so passers-by may come and look at it.
    this.garden.setVisitorsEnabled(true);

    if (this.visiting) {
      this.openVisit(this.visiting);
      this.refreshHud();
      this.progression.advance();
      return;
    }

    // Restore a previous garden, if there is one, into the new location.
    if (this.pendingRestore) {
      const report = this.garden.restore(
        this.pendingRestore.save,
        this.pendingRestore.lastSeenAt,
      );
      this.pendingRestore = null; // consumed; the live garden is now the truth
      this.hasSownOnce = true;
      this.hasGrownOnce = true;
      this.absorbCatchUp(report);
    } else {
      this.ui.showHint('Tap the soil to sow a seed', 4200);
    }

    this.refreshHud();
    this.refreshProgress();
  }

  /**
   * Fold a silent catch-up into the session: update everything the events
   * would normally have updated, then tell the player what the clock did.
   *
   * Below a couple of minutes there is nothing worth reporting and a modal
   * would just be a door to close, so it degrades to a toast and then to
   * silence.
   */
  private absorbCatchUp(report: OfflineReport): void {
    if (report.maturedWhileAway > 0) this.profile.maturedWhileAway = true;
    // Trees that came in overnight count toward the districts like any other.
    this.profile.treesGrown += report.maturedWhileAway;
    this.syncTier(false);

    // Mutations earned in absentia still count. They are derived from the
    // timeline, so they are already on the plants — this is only the record
    // that has to be told, and it is silent: no toast for a tree that came in
    // at four in the morning.
    for (const plant of report.matured) {
      const mutation = plant.mutation;
      if (mutation) this.mutations.add(mutation.id);
      // Challenges count trees that came in overnight; see `progression/challenges.ts`.
      this.progression.countSpecimen(plant);
    }
    this.profile.mutationsSeen = [...this.mutations];

    this.refreshHud();
    this.refreshProgress();
    this.persist();

    const away = formatDuration(report.awayMs);

    if (report.maturedWhileAway > 0) {
      const n = report.maturedWhileAway;
      const names = [...new Set(report.matured.map((p) => p.species.name))];
      this.ui.showAway(
        n === 1 ? 'A tree came in' : `${n} trees came in`,
        `You were gone ${away}. ${listOf(names)} reached full height without ` +
          'you — the rest are still on their way.',
        () => this.payOutAwayFacts(n),
      );
      return;
    }

    if (report.grewWhileAway > 0) {
      this.ui.showAway(
        'Your garden kept growing',
        `${report.grewWhileAway} growth stage${
          report.grewWhileAway === 1 ? '' : 's'
        } passed over the ${away} you were away. Nothing here is ever lost ` +
          'while you are gone.',
      );
      return;
    }

    if (report.awayMs > 120_000) {
      this.ui.toast('Welcome back', `${this.garden.plantCount} plants replanted here.`);
    }
  }

  /**
   * Hand out fact cards for trees that matured unattended.
   *
   * Capped at three. Twelve beds coming in overnight would otherwise stack
   * twelve modals, and the cards left unspent are not lost — they stay locked
   * and go to the next tree. Pacing the reward, never removing it.
   */
  private payOutAwayFacts(count: number): void {
    for (let i = 0; i < Math.min(3, count); i += 1) {
      const fact = nextFact(this.factsSeen);
      if (!fact) break;
      this.factsSeen.add(fact.id);
      window.setTimeout(
        () => this.ui.showFact(fact, this.factsSeen.size, TOTAL_FACTS),
        i * 220,
      );
    }
    this.refreshProgress();
    this.persist();
  }

  private moveGarden(): void {
    if (this.phase !== 'play') return;
    this.audio.tick();
    if (this.mode === 'preview') {
      // There is no other spot to move to; say so rather than dead-ending.
      this.ui.toast('Preview mode', 'Open on a phone to place your garden in a real space.');
      return;
    }
    // Keep the planting; only the anchor moves. `lastSeenAt` is now, so the
    // rebuild reports nothing — moving a plot is not time away.
    this.pendingRestore = { save: this.garden.serialize(), lastSeenAt: Date.now() };
    this.garden.unplace();
    this.phase = 'scanning';
    this.ui.show('scanning');
    this.ui.setScanText('Find a new spot for your garden');
  }

  /**
   * Come back from a backgrounded tab.
   *
   * `requestAnimationFrame` is suspended while hidden, so the garden is
   * frozen but the clock is not. Anything under half a minute the render loop
   * can absorb on its own without sounding wrong; past that it is a real
   * absence and gets the same silent catch-up as a cold start.
   */
  private resume(): void {
    const hiddenAt = this.hiddenAt;
    this.hiddenAt = null;
    if (this.phase !== 'play' || hiddenAt === null) return;
    if (Date.now() - hiddenAt < 30_000) return;
    this.absorbCatchUp(this.garden.catchUp(hiddenAt));
  }

  private disposeHorizon(): void {
    if (!this.horizon) return;
    this.scene.remove(this.horizon.root);
    this.horizon.dispose();
    this.horizon = null;
    this.scene.fog = null;
  }

  private handleSessionEnd(): void {
    this.disposeHorizon();
    this.audio.stopAmbience();
    this.phase = 'intro';
    this.ui.show('intro');
    this.ui.setBeginEnabled(true);
  }

  /* ---------------------------------------------------------------- *
   * Input
   * ---------------------------------------------------------------- */

  /**
   * Taps are resolved on pointer *up*, not down, and only when the pointer
   * barely moved. In preview mode the same gesture orbits the camera, and on
   * a phone a stray drag across the species rail would otherwise sow a seed
   * the player never asked for.
   */
  private pointerStart: { x: number; y: number; t: number } | null = null;

  private onPointerDown = (event: PointerEvent): void => {
    this.pointerStart = { x: event.clientX, y: event.clientY, t: performance.now() };
  };

  private onPointerUp = (event: PointerEvent): void => {
    const start = this.pointerStart;
    this.pointerStart = null;
    if (!start) return;
    if (this.phase !== 'play' || this.ui.isFactOpen || this.world.isOpen) {
      return;
    }

    /*
     * A visit is look-only. Sowing into somebody else's beds changes the thing
     * you came to see, and harvesting destroys it with no undo — the garden
     * exists for the length of a page view and there is nowhere to put a
     * correction. Orbiting still works, which is the part worth doing.
     */
    if (this.visiting) {
      this.ui.showHint('You are looking at someone else’s garden', 2200);
      return;
    }

    const moved = Math.hypot(event.clientX - start.x, event.clientY - start.y);
    if (moved > 12 || performance.now() - start.t > 600) return;

    // Anything inside an interactive overlay control is the UI's business.
    const target = event.target as HTMLElement | null;
    if (
      target?.closest(
        'button, input, label, .species-rail, .fact-card, .sheet, .scan-hint, .toast',
      )
    ) {
      return;
    }

    const ndc = new THREE.Vector2(
      (event.clientX / window.innerWidth) * 2 - 1,
      -(event.clientY / window.innerHeight) * 2 + 1,
    );

    this.garden.handleTap(ndc, this.activeCamera, this.ui.selectedSpecies);
  };

  private handleSpeciesChange(_id: SpeciesId): void {
    this.audio.tick();
  }

  private toggleMute(): void {
    const next = !this.audio.muted;
    this.audio.setMuted(next);
    this.ui.setMuted(next);
    if (!next) this.audio.tick();
    this.persist();
  }

  /* ---------------------------------------------------------------- *
   * Game events
   * ---------------------------------------------------------------- */

  private handleSow(plant: Plant): void {
    this.progression.bump('sown');
    if (!this.hasSownOnce) {
      this.hasSownOnce = true;
      this.ui.showHint(
        `${plant.species.name} sown — it grows on its own from here`,
        4600,
      );
    }
    this.refreshHud();
    this.refreshProgress();
    this.persist();
  }

  private handleGrow(plant: Plant, byTending: boolean): void {
    if (byTending) {
      // Only a tap counts as tending, which is what the Night Gardener badge
      // is actually about. A stage the clock turned over at 3am while the
      // phone sat in a pocket is not somebody gardening at night.
      this.profile.lastTendHour = new Date().getHours();
      if (!this.hasGrownOnce) {
        this.hasGrownOnce = true;
        this.ui.hideHint();
        this.ui.showHint('Tending speeds a plant along', 3000);
      }
    }
    if (plant.stage === STAGES - 1 && byTending) {
      this.ui.showHint('Nearly there…', 1400);
    }
    this.refreshHud();
    this.persist();
  }

  private handleAlreadyMature(): void {
    // Superseded by the harvest offer, which fires from the same tap and says
    // something more useful. Kept because `Garden` still announces it and a
    // silent event is a trap for whoever reads this next.
  }

  /* ---- Visiting somebody else's garden ----------------------------- */

  /**
   * Build the garden a link described. Nothing is written while this stands.
   */
  private openVisit(shared: SharedGarden): void {
    const now = Date.now();
    this.garden.setTier(shared.tier);
    this.garden.restore(
      {
        version: 2,
        plants: shared.plants.map((p, i) => ({
          speciesId: p.speciesId,
          slot: p.slot,
          seed: i + 1,
          sownAt: now - p.ageMinutes * 60000,
          tendMs: 0,
          tends: 0,
        })),
      },
      now,
    );
    this.ui.setVisiting(
      shared.name
        ? `Visiting ${shared.name}’s garden`
        : 'Visiting a garden somebody shared',
    );
  }

  /** Drop the link and come back to your own. A reload, so nothing leaks. */
  private leaveVisit(): void {
    const url = new URL(window.location.href);
    // Drop the seed and nothing else. Clearing the whole query string also
    // threw away `?mode=`, which silently changed which AR backend came back.
    url.searchParams.delete('g');
    window.location.replace(url.toString());
  }

  /* ---------------------------------------------------------------- *
   * The garden opening out
   * ---------------------------------------------------------------- */

  /**
   * Bring the plot up to whatever tier the player has earned.
   *
   * Returns true if it announced a district *and* claimed the fact-card slot,
   * so the caller knows not to open a second card on top of it.
   *
   * The tier is derived from `treesGrown` rather than stored, for the same
   * reason growth and weather are derived: there is then no second copy of
   * the truth to fall out of step. It is also why the counter is lifetime and
   * sticky — a district that closed because somebody harvested a tree would
   * be the worst kind of surprise.
   */
  private syncTier(announce: boolean): boolean {
    // The plot a visitor is looking at was sized by whoever shared it.
    if (this.visiting) return false;
    const want = tierFor(this.profile.treesGrown);
    if (!this.garden.setTier(want)) return false;

    this.backend?.setContentHeight?.(this.garden.contentHeight, this.garden.radius);
    if (!announce) return false;

    const tier = TIERS[want];
    this.audio.bloom(-5);
    this.particles.burst(
      this.garden.root.getWorldPosition(new THREE.Vector3()),
      0x8fd3c7,
      64,
      1.8,
    );
    this.ui.toast(`${tier.name} · ${tier.nameAr}`, tier.blurb, 5200);

    // Each district hands over the sourced card that explains the real one.
    // The expansion is the teaching device, exactly as a mutation is.
    const fact = tier.factId ? factById(tier.factId) : null;
    if (!fact || this.factsSeen.has(fact.id)) return false;
    this.factsSeen.add(fact.id);
    window.setTimeout(() => this.ui.showFact(fact, this.factsSeen.size, TOTAL_FACTS), 2400);
    return true;
  }

  /* ---------------------------------------------------------------- *
   * Harvest and the seed economy
   * ---------------------------------------------------------------- */

  /**
   * First tap on a mature specimen. Nothing has happened yet.
   *
   * The prompt names the tree and the price so the second tap is never a
   * surprise. Harvest is the only destructive act in the game and it is
   * entirely optional — a player who wants a permanent grove simply never
   * takes the second tap, and the offer expires on its own.
   */
  private handleHarvestOffer(plant: Plant, paid: number): void {
    const mark = plant.mutation ? `${plant.mutation.name} ` : '';
    this.ui.showHint(
      `Tap again to harvest this ${mark}${plant.species.name} · +${paid} 🌰`,
      6000,
    );
  }

  private handleHarvest(plant: Plant, paid: number): void {
    this.profile.seedlings += paid;
    this.profile.harvested += 1;

    const speciesId = plant.species.id as SpeciesId;
    const mutation = plant.mutation;

    // Sticky records, because the plant is about to stop existing and none of
    // this is recoverable from the garden afterwards.
    this.speciesGrown.add(speciesId);
    if (mutation) this.mutations.add(mutation.id);

    /*
     * Credit the gardener only for a specimen they grew. The standing-plant
     * score already works this way, so without the same guard here, signing
     * the register and immediately lifting the previous gardener's grove
     * would be the highest-scoring move in the game.
     */
    const stamp = this.activeStamp;
    if (stamp && plant.sownAt >= stamp.baseline.sinceMs) {
      stamp.harvested += 1;
      stamp.harvestScore += pointsFor({ species: speciesId, mutation: mutation?.id ?? null });
      if (!stamp.harvestSpecies.includes(speciesId)) stamp.harvestSpecies.push(speciesId);
    }

    this.ui.hideHint();
    this.ui.toast(
      `${plant.species.name} harvested`,
      `+${paid} seedlings banked. The bed is free again.`,
      2600,
    );

    this.refreshHud();
    this.refreshProgress();
    this.persist();
  }

  /**
   * Spend seedlings on a pack and put whatever it drew straight in the ground.
   *
   * The draw follows the rarity table in `content/species.ts`, so a run of
   * packs is mostly umbrella thorn and ghaf — which is the point. The ladder
   * the species rail shows is the ladder the draw actually runs, and the
   * desert rose turning up is rare for the reason the card gives.
   */
  private openPack(): void {
    if (this.phase !== 'play' || this.visiting) return;
    if (this.profile.seedlings < PACK_COST || this.garden.freeSlots === 0) return;

    this.audio.tick();
    this.profile.seedlings -= PACK_COST;
    this.progression.bump('packs');

    const drawn = drawPack(Math.random, this.garden.freeSlots);
    // Stagger so three seeds ring as three notes rather than one chord, and
    // so the particle bursts read as three separate events.
    drawn.forEach((id, i) => {
      window.setTimeout(() => {
        this.garden.sowSpecies(SPECIES[id]);
        if (i === drawn.length - 1) {
          this.refreshHud();
          this.refreshProgress();
          this.persist();
        }
      }, i * 260);
    });

    const names = drawn.map((id) => SPECIES[id].name);
    const special = drawn.filter((id) => id === PACK_DRAW.special.id).length;
    this.ui.toast(
      special > 0 ? PACK_DRAW.special.toastTitle : 'Seed pack opened',
      `${names.join(', ')} — sown.`,
      3200,
    );

    this.refreshHud();
    this.persist();
  }

  private handleMature(plant: Plant, treeCount: number): void {
    this.ui.setTreeCount(treeCount);
    /*
     * A borrowed garden goes on growing while it is being looked at, and it
     * should — that is the point of encoding ages rather than stages. But the
     * tree that comes in is not the visitor's, so it must not move their
     * lifetime count, and they must certainly not be asked to sign a register
     * for it. The bloom and the particles are `Garden`'s and still happen.
     */
    if (this.visiting) return;

    this.profile.treesGrown += 1;
    this.progression.countSpecimen(plant);

    const firstOfSpecies =
      Array.from(this.garden.collectedSpecies).filter((id) => id === plant.species.id)
        .length === 1 &&
      this.countMatureOfSpecies(plant.species.id as SpeciesId) === 1;

    const mutation = plant.mutation;
    // A district opening is the biggest thing that can happen in a session,
    // so it outranks both the mark and the species note for the one card slot.
    let cardShown = this.syncTier(true);

    if (!cardShown && mutation) {
      // The mark outranks the species note — it is the rarer event, and two
      // toasts fighting over the same corner is worse than losing one.
      cardShown = this.handleMutation(mutation, plant.species.name);
    } else if (!cardShown && firstOfSpecies) {
      this.ui.toast(`${plant.species.name} · ${plant.species.nameAr}`, plant.species.blurb, 4600);
    }

    // Every matured tree reveals the next unseen card, so the library keeps
    // paying out for as long as the player keeps planting — unless a first
    // mutation has already claimed the slot with the card that explains it.
    const fact = cardShown ? null : nextFact(this.factsSeen);
    if (fact) {
      this.factsSeen.add(fact.id);
      // Let the bloom animation and toast land before interrupting.
      window.setTimeout(
        () => this.ui.showFact(fact, this.factsSeen.size, TOTAL_FACTS),
        firstOfSpecies ? 1500 : 700,
      );
    }

    /*
     * Ask for a name only once the player has something worth signing for.
     * Prompting at the door — before anything has grown — is the thing that
     * makes a visitors' book feel like a form.
     */
    if (treeCount >= 3 && !this.activeStamp && !this.signPrompted) {
      this.signPrompted = true;
      window.setTimeout(() => this.ui.showSign(), 2600);
    }

    this.refreshHud();
    this.refreshProgress();
    this.persist();
  }

  private signPrompted = false;

  /** "6 to Charbagh Oasis", or null once every district is open. */
  private nextDistrictLine(): string | null {
    const next = nextTier(this.profile.treesGrown);
    if (!next) return null;
    return `${next.remaining} to ${next.tier.name}`;
  }

  private countMatureOfSpecies(id: SpeciesId): number {
    return this.garden.all.filter((p) => p.isMature && p.species.id === id).length;
  }

  /** Info button: re-read a card you have already unlocked. */
  private refreshHud(): void {
    this.backend?.setContentHeight?.(this.garden.contentHeight, this.garden.radius);
    this.ui.setSeedlings(
      this.profile.seedlings,
      PACK_COST,
      PACK_SIZE,
      this.profile.seedlings < PACK_COST
        ? `${PACK_COST - this.profile.seedlings} more to open a pack`
        : this.garden.freeSlots === 0
          ? 'No free beds — harvest one first'
          : null,
      // Levels pay seedlings too, so the economy can appear before a harvest.
      this.profile.harvested > 0 || this.profile.levelClaimed > 1,
    );
    const planted = this.garden.plantCount;
    const capacity = this.garden.capacity;
    const streak = this.streak;
    this.ui.setTreeCount(this.garden.treeCount);
    this.ui.setProgress(
      planted,
      capacity,
      planted === 0
        ? 'Tap the soil to sow your first seed'
        : [
            `${planted} of ${capacity} beds`,
            `${this.speciesGrown.size} of ${SPECIES_ORDER.length} species`,
            this.nextDistrictLine(),
            streak > 1 ? `${streak}-day streak` : null,
          ]
            .filter(Boolean)
            .join(' · '),
    );
    this.updateCountdown();
  }

  /**
   * The "next tree in …" line.
   *
   * Recomputed on a one-second cadence from the render loop rather than on a
   * timer, so it costs nothing when the tab is backgrounded and can never
   * drift from what the plants are actually doing.
   */
  private updateCountdown(): void {
    if (this.phase !== 'play') return;
    const now = Date.now();
    const next = this.garden.nextReady(now);
    if (!next) {
      this.ui.setNextReady(
        this.garden.plantCount === 0
          ? null
          : 'Every plant is fully grown. Sow another, or come back with more beds.',
      );
      return;
    }
    this.ui.setNextReady(
      `Next ${next.species.name} in ${formatDuration(next.remainingMs(now))} · tap to tend`,
    );
  }

  /* ---------------------------------------------------------------- *
   * Weather
   * ---------------------------------------------------------------- */

  /**
   * Ease the scene toward the current sky and sprinkle its motes.
   *
   * The lighting lerps rather than cuts, because weather turns over on a
   * four-minute boundary that the player has no reason to expect — a hard
   * switch in the middle of looking at a plant reads as a rendering glitch.
   */
  private updateWeather(dt: number): void {
    const weather = weatherAt(Date.now());
    this.ui.setWeather(weather);

    if (!this.weatherSeen.has(weather.id)) {
      this.weatherSeen.add(weather.id);
      this.profile.weatherSeen = [...this.weatherSeen];
      if (weather.id !== 'clear') {
        this.ui.toast(`${weather.glyph}  ${weather.name}`, weather.note, 3800);
      }
      this.persist();
    }

    // ~1.8s to settle. Frame-rate independent, like every other tween here.
    const k = 1 - Math.pow(0.12, dt);
    this.horizon?.setPalette(
      weather.skyTop,
      weather.skyBottom,
      weather.ground,
      weather.exposure,
      dt,
    );
    /*
     * Lanterns come up as the sky goes down. Driving them off the weather's
     * own exposure rather than a clock means a sandstorm lights the pavilion
     * for the same reason it darkens everything else, and the two never
     * disagree.
     */
    this.garden.setNight(THREE.MathUtils.clamp((1.05 - weather.exposure) * 2.6, 0, 1));

    /*
     * The real room, on top of the weather rather than instead of it.
     *
     * The garden keeps the sky it grew under — a sandstorm is still amber —
     * and the measured light leans it toward the space it is standing in.
     * Both are multipliers, so neither can win outright, and `xrLighting`
     * clamps its side hard enough that a bad reading cannot wash the scene
     * out. Null everywhere except `webxr` with a granted probe.
     */
    const real = this.backend?.lightSample?.() ?? null;
    const sunScale = real ? real.sun : 1;
    const ambientScale = real ? real.ambient : 1;

    this.hemi.color.lerp(new THREE.Color(weather.sky), k);
    this.hemi.groundColor.lerp(new THREE.Color(weather.ground), k);
    this.sun.color.lerp(new THREE.Color(weather.sky), k);
    if (real) {
      // Lean the key light toward the room's own colour, a third of the way.
      this.sun.color.multiply(
        new THREE.Color(real.colour[0], real.colour[1], real.colour[2]),
      );
    }
    this.hemi.intensity +=
      (this.baseHemi * weather.exposure * ambientScale - this.hemi.intensity) * k;
    this.sun.intensity +=
      (this.baseSun * weather.exposure * sunScale - this.sun.intensity) * k;

    /*
     * Point the key light along the measured one.
     *
     * This is the part that matters most outdoors: shadows falling the same
     * way as the real ones is most of what stops the garden reading as pasted
     * on. The light is moved rather than rotated because it targets the plot,
     * and the distance is fixed so the shadow frustum — tuned tight on
     * purpose, see `setupLighting` — keeps containing the garden.
     */
    if (real?.direction) {
      const [dx, dy, dz] = real.direction;
      const focus = this.garden.isPlaced ? this.garden.root.position : this.sunTarget.position;
      // Never let the key light drop to or below the ground plane, or the
      // plot is lit from underneath and every shadow inverts.
      const lift = Math.max(0.35, dy);
      this.sun.position.set(
        focus.x + dx * 3.4,
        focus.y + lift * 3.4,
        focus.z + dz * 3.4,
      );
    }

    if (!weather.motes || !this.garden.isPlaced) return;

    /*
     * Emit on an accumulator rather than per frame, so a 90fps headset and a
     * 30fps phone see the same rainfall rather than three times as much.
     */
    const storm = weather.id === 'sandstorm';

    /*
     * Weather has to fall on the whole garden, and the garden grows.
     *
     * The spawn radius was authored as a flat metre against the 0.7 m terrace
     * this started as. Once the districts opened, that covered half the plot's
     * radius at the wetland tier — measured — so the wadi and the wetland sat
     * in a downpour that visibly stopped short of them.
     *
     * The rate rises with the radius rather than with the area, which is a
     * deliberate under-correction: matching area would be five times the motes
     * at full size, and the pool is 320 shared with every burst in the game.
     * A big garden is a little sparser than a small one, which reads as scale
     * rather than as a bug.
     */
    const spread = this.garden.radius / TIERS[0].radius;
    this.moteAccumulator += dt * (storm ? 60 : 105) * Math.min(spread, 1.9);
    const count = Math.floor(this.moteAccumulator);
    if (count <= 0) return;
    this.moteAccumulator -= count;
    /*
     * Rain is dense, fast and nearly vertical; dust is sparser, slower and
     * mostly sideways. The first pass gave both the same lazy drift and a
     * spawn ceiling near a metre, so the motes died in mid-air before they
     * reached anything and the downpour read as a cloud of fireflies.
     */
    this.particles.fall(
      this.garden.root.position,
      this.garden.radius * (storm ? 1.43 : 1.14),
      storm ? 0.42 : 0.62,
      weather.motes,
      count,
      storm ? 0.7 : 0.05,
      storm ? 0.35 : 1.5,
    );
  }

  /**
   * A newly matured specimen carrying a mark.
   *
   * The first of each kind delivers the card explaining the real phenomenon
   * behind it — the mutation is the teaching device, which is the whole
   * reason the weather is grounded in conditions a real garden has to survive
   * rather than invented. Golden hour deliberately carries no card: it is tied to
   * the player's own clock and claims nothing factual.
   */
  private handleMutation(mutation: Mutation, speciesName: string): boolean {
    const firstEver = !this.mutations.has(mutation.id);
    this.mutations.add(mutation.id);
    this.profile.mutationsSeen = [...this.mutations];
    this.ui.mutationToast(mutation, speciesName);

    if (!firstEver) return false;
    const weather = WEATHER_ORDER.map((id) => WEATHER[id]).find(
      (w) => w.mutation === mutation.id,
    );
    const fact = weather?.factId ? factById(weather.factId) : null;
    if (!fact || this.factsSeen.has(fact.id)) return false;

    this.factsSeen.add(fact.id);
    window.setTimeout(
      () => this.ui.showFact(fact, this.factsSeen.size, TOTAL_FACTS),
      2400,
    );
    return true;
  }

  /* ---------------------------------------------------------------- *
   * Progression — badges, score, the register
   * ---------------------------------------------------------------- */

  private get streak(): number {
    return currentStreak(this.profile.days, Date.now());
  }

  private get activeStamp() {
    return this.profile.register.find((s) => s.id === this.profile.activeStampId) ?? null;
  }

  /**
   * Re-evaluate every badge rule and update the signed gardener's entry.
   *
   * Badges are recomputed from scratch rather than incremented at each event:
   * the rules then live in exactly one place, and adding one later
   * retroactively awards it to players who already qualified.
   */
  private refreshProgress(): void {
    /*
     * Nothing about a visit belongs to the visitor.
     *
     * Everything below reads the *standing garden* and writes it into the
     * player's own profile — species grown, mutations seen, the lifetime tree
     * count, badges, and the register entry. During a visit the standing
     * garden is somebody else's, so without this guard a visitor would be
     * awarded a Botanist toast for a grove they did not plant, and the tier —
     * derived from that same tree count — could snap the borrowed garden from
     * the wetland back to a bare terrace while they were looking at it.
     *
     * The writes were already dead (see `persist`), so this was never a way to
     * lose your save. It was a way to be told something untrue about it.
     */
    if (this.visiting) return;

    /*
     * Fold in whatever is currently standing in the garden before judging
     * anything. A mutation is derived from the timeline rather than stored on
     * the plant, so the only record that one was ever grown is this set — and
     * it was previously only ever added to by the two live event paths. A
     * garden restored from a save went uncounted. Recomputing from the ground
     * truth here gives the set the same self-healing property the badge rules
     * already have.
     */
    for (const plant of this.garden.all) {
      const mutation = plant.mutation;
      if (mutation) this.mutations.add(mutation.id);
      if (plant.isMature) this.speciesGrown.add(plant.species.id as SpeciesId);
    }
    this.profile.speciesGrown = [...this.speciesGrown];

    /*
     * Self-heal the lifetime tree count the same way. A save written before
     * districts existed has no counter at all, and a returning player with a
     * grove already standing should not be sent back to a bare terrace. It
     * only ever ratchets up.
     */
    this.profile.treesGrown = Math.max(
      this.profile.treesGrown,
      this.garden.treeCount + this.profile.harvested,
    );
    this.syncTier(false);

    const earned = earnedBadges({
      planted: this.garden.plantCount,
      capacity: this.garden.capacity,
      trees: this.garden.treeCount,
      // Sticky rather than standing: the Botanist badge should not be taken
      // back because the sidr that earned it has since been harvested.
      species: this.speciesGrown,
      facts: this.factsSeen.size,
      totalFacts: TOTAL_FACTS,
      streak: this.streak,
      maturedWhileAway: this.profile.maturedWhileAway,
      returned: this.profile.days.length > 1,
      lastTendHour: this.profile.lastTendHour,
      mutations: this.mutations,
      harvested: this.profile.harvested,
    });

    const fresh = earned.filter((id) => !this.badges.has(id));
    for (const id of earned) this.badges.add(id);
    this.profile.badges = [...this.badges];

    // `UI.prize` queues and paces these, and keeps them ahead of any level
    // they helped earn, which `progression.advance` announces next.
    for (const id of fresh) {
      const badge = BADGES_BY_ID.get(id);
      if (badge) this.ui.badgeToast(badge);
    }

    this.progression.advance();
    this.updateStamp();
  }

  /**
   * Keep the signed gardener's register entry in step with the garden.
   *
   * Everything here is measured against the stamp's baseline, so a gardener
   * who takes over a full plot scores what *they* grow, not what they were
   * handed. See `StampBaseline`.
   */
  private updateStamp(): void {
    const stamp = this.activeStamp;
    if (!stamp) return;

    const mine = this.garden.all.filter(
      (p) => p.isMature && p.sownAt >= stamp.baseline.sinceMs,
    );
    const mature = mine.map((p) => ({
      species: p.species.id as SpeciesId,
      mutation: p.mutation?.id ?? null,
    }));
    const badges = [...this.badges];

    stamp.lastSeenAt = Date.now();
    // A harvested tree still counts as one this gardener grew. Anything else
    // would make lifting a specimen a punishment for using the mechanic.
    stamp.trees = mature.length + stamp.harvested;
    stamp.species = new Set([
      ...mature.map((p) => p.species),
      ...stamp.harvestSpecies,
    ]).size;
    stamp.facts = Math.max(0, this.factsSeen.size - stamp.baseline.facts);
    stamp.badges = badges;
    stamp.streak = this.streak;
    stamp.score = scoreFor(
      mature,
      stamp.species,
      stamp.facts,
      Math.max(0, badges.length - stamp.baseline.badges),
      stamp.harvestScore,
    );
  }

  private showRegister(): void {
    this.audio.tick();
    this.updateStamp();
    this.ui.showRegister({
      ranked: rankRegister(this.profile.register),
      activeId: this.profile.activeStampId,
      streak: this.streak,
    });
  }

  private signRegister(name: string, from: string): void {
    const clean = sanitiseField(name, MAX_NAME);
    if (!clean) {
      this.ui.toast('Nothing entered', 'Add a name to sign the register.');
      return;
    }

    const now = Date.now();
    // The first signature on a device claims the whole garden; every one
    // after it starts from where the previous gardener left off.
    const isFirst = this.profile.register.length === 0;
    const stamp = makeStamp(
      clean,
      sanitiseField(from, MAX_FROM),
      now,
      this.streak,
      isFirst
        ? { sinceMs: 0, facts: 0, badges: 0 }
        : { sinceMs: now, facts: this.factsSeen.size, badges: this.badges.size },
    );
    this.profile.register.push(stamp);
    this.profile.activeStampId = stamp.id;
    this.updateStamp();
    this.persist();

    this.audio.bloom(0);
    this.ui.toast(
      `Signed — ${stamp.name}`,
      stamp.from
        ? `Recorded as a gardener from ${stamp.from}.`
        : 'Recorded in this garden’s register.',
    );
  }

  /* ---------------------------------------------------------------- *
   * Persistence
   * ---------------------------------------------------------------- */

  private snapshot(): SavedProfile {
    this.profile.garden = this.garden.serialize();
    this.profile.factsSeen = [...this.factsSeen];
    this.profile.muted = this.audio.muted;
    this.profile.badges = [...this.badges];
    this.profile.mutationsSeen = [...this.mutations];
    this.profile.speciesGrown = [...this.speciesGrown];
    this.profile.weatherSeen = [...this.weatherSeen];
    this.profile.lastSeenAt = Date.now();
    return this.profile;
  }

  /*
   * Both write paths are disabled for the whole of a visit.
   *
   * This is the guarantee the share feature rests on: opening a friend's link
   * must not cost you your own garden. Guarding here rather than at each call
   * site means a new one cannot forget — and there is no path that writes
   * without going through one of these two.
   */
  private persist(): void {
    if (this.visiting) return;
    this.save(this.snapshot());
  }

  /** Write immediately, skipping the debounce. Used when the tab is going away. */
  private flush(): void {
    if (this.visiting || this.phase !== 'play') return;
    this.updateStamp();
    saveProfile(this.snapshot());
  }

  /* ---------------------------------------------------------------- *
   * Loop
   * ---------------------------------------------------------------- */

  /**
   * The camera the world is actually being rendered with.
   *
   * Under WebXR the renderer drives its own; ours is the one it is not. Both
   * raycasting a tap and turning the visitors' reaction bubbles to face the
   * player have to use this one, or they are aimed at a camera nobody is
   * looking through.
   */
  private get activeCamera(): THREE.Camera {
    return this.renderer.xr.isPresenting ? this.renderer.xr.getCamera() : this.camera;
  }

  private tick = (time: number, frame?: XRFrame): void => {
    this.timer.update(time);
    // Cap the step so a backgrounded tab does not resume with a huge delta
    // and fling every tween past its target.
    const dt = Math.min(this.timer.getDelta(), 0.05);
    const elapsed = this.timer.getElapsed();

    this.backend?.update(frame ?? null);

    if (this.phase === 'scanning') {
      const hit = this.backend?.hitTest() ?? null;
      if (hit) {
        this.reticle.setVisible(true);
        this.reticle.place(hit.position, hit.quaternion);
        this.ui.setPlaceEnabled(true);
      } else {
        this.reticle.setVisible(false);
        this.ui.setPlaceEnabled(false);
      }
      this.reticle.update(dt, elapsed);
    }

    this.horizon?.update(dt, elapsed);
    this.garden.update(dt, elapsed, this.activeCamera);
    this.particles.update(dt);

    // Plants grow continuously between taps, so the framing has to track the
    // tween, not just the discrete events that start it.
    if (this.phase === 'play') {
      this.backend?.setContentHeight?.(this.garden.contentHeight);
      this.updateWeather(dt);

      if (elapsed - this.lastCountdown > 1) {
        this.lastCountdown = elapsed;
        this.updateCountdown();
      }

      /*
       * Heartbeat. Growth is measured from `lastSeenAt`, and a player who
       * simply watches the garden for twenty minutes fires no events at all —
       * without this, closing the tab would leave a save claiming they had
       * been away since their last tap.
       */
      if (elapsed - this.lastHeartbeat > 20) {
        this.lastHeartbeat = elapsed;
        // A session left open past midnight rolls into the new day here.
        if (!this.visiting && this.phase === 'play') {
          this.progression.noteVisit(Date.now());
          this.progression.advance();
        }
        this.updateStamp();
        this.persist();
      }
    }

    this.renderer.render(this.scene, this.camera);
  };

  private onResize = (): void => {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(window.innerWidth, window.innerHeight);
  };
}

/** "A ghaf", "A ghaf and a sidr", "A ghaf, a sidr and 2 others". */
function listOf(names: string[]): string {
  if (names.length === 0) return 'Nothing';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

function describeStartError(err: unknown): string {
  const name = (err as { name?: string })?.name ?? '';
  if (name === 'NotAllowedError') {
    return 'Camera access was declined. Allow the camera and reload to plant your garden.';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'No usable camera was found on this device.';
  }
  if (name === 'NotReadableError') {
    return 'The camera is already in use by another app. Close it and try again.';
  }
  return 'AR could not start on this device. Try Chrome on Android or Safari on iOS.';
}

new App();
